import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Car, User, FileText, Truck, Calculator, ShieldCheck, Download } from "lucide-react";
import { DEAL_STAGES, formatCurrency } from "@/types/crm";
import type { Deal, DealStage } from "@/types/crm";
import { exportToExcel } from "@/utils/excelExport";
import { logAudit, computeChanges } from "@/utils/auditLog";
import { useAuth } from "@/hooks/useAuth";
import { generateBillOfSale } from "@/utils/billOfSalePdf";
import type { BillOfSaleData } from "@/utils/billOfSalePdf";
import BillOfSalePreview from "@/components/admin/BillOfSalePreview";
import { validateDealStageTransition } from "@/utils/validation";
import { useKeyboardShortcut } from "@/hooks/useKeyboardShortcut";

interface LineItem { id: string; name: string; amount: number; }

interface WorksheetData {
  selling_price: number;
  omvic_fee: number;
  hst_rate: number;
  hst_amount: number;
  license_fee: number;
  gasoline: number;
  deposit_amount: number;
  deposit_method: string;
  lien_registration_fee: number;
  rst_on_insurance: number;
  warranties: LineItem[];
  fees: LineItem[];
  accessories: LineItem[];
  insurances: LineItem[];
}

interface TradeInInfo {
  has_trade_in: boolean;
  year: string;
  make: string;
  model: string;
  body_type: string;
  trim_level: string;
  colour: string;
  vin: string;
  exact_distance: string;
  distance_type: 'kms' | 'miles';
  hst_registrant_no: string;
  has_lien: boolean;
  lien_holder: string;
  lien_amount: number;
  trade_in_allowance: number;
}

interface FinancingInfo {
  is_financing: boolean;
  financing_type: 'third_party' | 'dealer' | '';
  date_of_advance: string;
  interest_start_date: string;
  regular_payments: number;
  fixed_rate: number;
  term_of_loan: number;
  amortization_period: number;
  total_number_of_payments: number;
  payment_frequency: string;
  date_of_first_payment: string;
  date_of_last_payment_amount: string;
  remaining_balance: number;
  total_of_all_payments: number;
  total_interest: number;
  apr: number;
  dealer_incentive: boolean;
}

interface GuarantyInfo {
  has_dealer_guaranty: boolean;
  days_or_km: string;
  description: string;
  has_extended_warranty: boolean;
  retail_value: number;
  company: string;
  months_or_km: string;
  warranty_description: string;
}

interface InsuranceInfo {
  company: string;
  policy_no: string;
  expiry_date: string;
  agent_and_phone: string;
}

interface DeliveryData {
  delivery_date: string;
  delivery_notes: string;
  is_delivered: boolean;
  mfrs_warranty_date: string;
  safety_certificate: boolean;
  daily_rental: boolean;
  mto_brand: string;
}

interface ExpandedNotes {
  disclosures: string;
  comments: string;
  salesperson_name: string;
  salesperson_reg_no: string;
  delivery: DeliveryData;
  trade_in: TradeInInfo;
  financing: FinancingInfo;
  guaranty: GuarantyInfo;
  insurance: InsuranceInfo;
}

const uid = () => Math.random().toString(36).slice(2, 9);

const defaultWorksheet: WorksheetData = {
  selling_price: 0, omvic_fee: 0, hst_rate: 13, hst_amount: 0, license_fee: 0, gasoline: 0,
  deposit_amount: 0, deposit_method: 'credit_card', lien_registration_fee: 0, rst_on_insurance: 0,
  warranties: [], fees: [], accessories: [], insurances: [],
};

const defaultExpanded: ExpandedNotes = {
  disclosures: "", comments: "",
  salesperson_name: "", salesperson_reg_no: "",
  delivery: { delivery_date: "", delivery_notes: "", is_delivered: false, mfrs_warranty_date: "", safety_certificate: false, daily_rental: false, mto_brand: "None" },
  trade_in: { has_trade_in: false, year: "", make: "", model: "", body_type: "", trim_level: "", colour: "", vin: "", exact_distance: "", distance_type: "kms", hst_registrant_no: "", has_lien: false, lien_holder: "", lien_amount: 0, trade_in_allowance: 0 },
  financing: { is_financing: false, financing_type: "third_party", date_of_advance: "", interest_start_date: "", regular_payments: 0, fixed_rate: 0, term_of_loan: 0, amortization_period: 0, total_number_of_payments: 0, payment_frequency: "Monthly", date_of_first_payment: "", date_of_last_payment_amount: "", remaining_balance: 0, total_of_all_payments: 0, total_interest: 0, apr: 0, dealer_incentive: false },
  guaranty: { has_dealer_guaranty: false, days_or_km: "", description: "", has_extended_warranty: false, retail_value: 0, company: "", months_or_km: "", warranty_description: "" },
  insurance: { company: "", policy_no: "", expiry_date: "", agent_and_phone: "" }
};

function parseWorksheet(fiNotes: string | null): WorksheetData {
  if (!fiNotes) return { ...defaultWorksheet };
  try {
    const parsed = JSON.parse(fiNotes);
    // Migration from old purchase_price to selling_price
    if (parsed.purchase_price !== undefined && parsed.selling_price === undefined) {
      parsed.selling_price = parsed.purchase_price;
    }
    return { ...defaultWorksheet, ...parsed };
  } catch { return { ...defaultWorksheet }; }
}

function parseExpandedNotes(notes: string | null): ExpandedNotes {
  if (!notes) return { ...defaultExpanded };
  try {
    const parsed = JSON.parse(notes);
    return {
      disclosures: parsed.disclosures || "",
      comments: parsed.comments || "",
      salesperson_name: parsed.salesperson_name || "",
      salesperson_reg_no: parsed.salesperson_reg_no || "",
      delivery: { ...defaultExpanded.delivery, ...parsed.delivery },
      trade_in: { ...defaultExpanded.trade_in, ...parsed.trade_in },
      financing: { ...defaultExpanded.financing, ...parsed.financing },
      guaranty: { ...defaultExpanded.guaranty, ...parsed.guaranty },
      insurance: { ...defaultExpanded.insurance, ...parsed.insurance }
    };
  } catch { return { ...defaultExpanded, disclosures: notes }; }
}

interface DealFormProps { deal: Deal | null; onBack: () => void; onSaved: () => void; }

export default function DealForm({ deal, onBack, onSaved }: DealFormProps) {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState("customers");

  // Bill of Sale preview state
  const [billOfSaleOpen, setBillOfSaleOpen] = useState(false);
  const [billOfSalePdf, setBillOfSalePdf] = useState<Uint8Array | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Core fields
  const [title, setTitle] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [vehicleId, setVehicleId] = useState("none");
  const [stage, setStage] = useState<string>("prospect");

  // State objects
  const [worksheet, setWorksheet] = useState<WorksheetData>({ ...defaultWorksheet });
  const [expanded, setExpanded] = useState<ExpandedNotes>({ ...defaultExpanded });

  useEffect(() => { loadOptions(); }, []);

  useEffect(() => {
    if (deal) {
      setTitle(deal.title || "");
      setCustomerId(deal.customer_id || "");
      setVehicleId(deal.vehicle_id || "none");
      setStage(deal.stage || "prospect");
      setWorksheet(parseWorksheet(deal.fi_notes));
      setExpanded(parseExpandedNotes(deal.notes));
    }
  }, [deal]);

  const handleStageChange = (newStage: string) => {
    if (deal) {
      const result = validateDealStageTransition(deal.stage as any, newStage as any);
      if (!result.allowed) {
        toast.error(result.reason || "Invalid stage transition");
        return;
      }
    }
    setStage(newStage);
  };

  const loadOptions = async () => {
    const [cRes, vRes] = await Promise.all([
      supabase.from("customers").select("*").order("first_name"),
      supabase.from("vehicles").select("*").order("year", { ascending: false }),
    ]);
    if (cRes.data) setCustomers(cRes.data);
    if (vRes.data) setVehicles(vRes.data);
  };

  // Worksheet updates
  const updateWorksheet = useCallback((field: keyof WorksheetData, value: any) => {
    setWorksheet(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateExpanded = useCallback((section: keyof ExpandedNotes, field: string, value: any) => {
    setExpanded(prev => {
      if (typeof prev[section] === 'object' && prev[section] !== null) {
         return { ...prev, [section]: { ...(prev[section] as any), [field]: value } };
      }
      return { ...prev, [section]: value };
    });
  }, []);

  const addLineItem = (section: "warranties" | "fees" | "accessories" | "insurances") => {
    setWorksheet(p => ({ ...p, [section]: [...p[section], { id: uid(), name: "", amount: 0 }] }));
  };
  const updateLineItem = (section: "warranties" | "fees" | "accessories" | "insurances", id: string, field: "name" | "amount", value: any) => {
    setWorksheet(p => ({ ...p, [section]: p[section].map(i => i.id === id ? { ...i, [field]: field === "amount" ? Number(value) || 0 : value } : i) }));
  };
  const removeLineItem = (section: "warranties" | "fees" | "accessories" | "insurances", id: string) => {
    setWorksheet(p => ({ ...p, [section]: p[section].filter(i => i.id !== id) }));
  };

  // Math Logic (Bill of Sale strictly)
  const extWarrantyTotal = worksheet.warranties.reduce((s, i) => s + i.amount, 0);
  const feesTotal = worksheet.fees.reduce((s, i) => s + i.amount, 0);
  const accessoriesTotal = worksheet.accessories.reduce((s, i) => s + i.amount, 0);
  const insurancesTotal = worksheet.insurances.reduce((s, i) => s + i.amount, 0);

  const subTotal1 = worksheet.selling_price + extWarrantyTotal + worksheet.omvic_fee + feesTotal + accessoriesTotal;
  const netDifference = subTotal1 - (expanded.trade_in.has_trade_in ? expanded.trade_in.trade_in_allowance : 0);
  const hstAmount = Math.max(0, Math.round((netDifference * worksheet.hst_rate) / 100 * 100) / 100);
  
  const payoutLien = expanded.trade_in.has_trade_in && expanded.trade_in.has_lien ? expanded.trade_in.lien_amount : 0;
  
  const subTotal2 = netDifference + hstAmount + worksheet.license_fee + worksheet.gasoline + payoutLien;
  const payableOnDelivery = subTotal2 - worksheet.deposit_amount;
  
  const totalPrincipalLoan = payableOnDelivery + insurancesTotal + worksheet.rst_on_insurance + worksheet.lien_registration_fee;
  
  const costOfBorrowing = expanded.financing.is_financing ? expanded.financing.total_interest : 0;
  const totalBalanceDue = totalPrincipalLoan + costOfBorrowing;

  const handleSubmit = async () => {
    if (!customerId) return toast.error("Select customer");
    if (!title) return toast.error("Enter title");

    // Client-side validations (CRIT-3)
    if ((stage === "funded" || stage === "delivered") && (vehicleId === "none" || !vehicleId)) {
      return toast.error("A vehicle must be selected for funded or delivered deals");
    }

    if (
      worksheet.selling_price < 0 ||
      worksheet.omvic_fee < 0 ||
      worksheet.license_fee < 0 ||
      worksheet.gasoline < 0 ||
      worksheet.deposit_amount < 0 ||
      worksheet.lien_registration_fee < 0 ||
      worksheet.rst_on_insurance < 0
    ) {
      return toast.error("Financial values and fees cannot be negative");
    }

    const hasNegativeLineItems = [
      ...worksheet.warranties,
      ...worksheet.fees,
      ...worksheet.accessories,
      ...worksheet.insurances
    ].some(item => item.amount < 0);
    if (hasNegativeLineItems) {
      return toast.error("Line item amounts cannot be negative");
    }

    if (expanded.trade_in.has_trade_in) {
      if (expanded.trade_in.trade_in_allowance < 0 || expanded.trade_in.lien_amount < 0) {
        return toast.error("Trade-in allowance and lien amounts cannot be negative");
      }
      if (!expanded.trade_in.vin?.trim()) {
        return toast.error("Trade-in VIN is required");
      }
    }

    if (expanded.financing.is_financing) {
      if (
        expanded.financing.regular_payments < 0 ||
        expanded.financing.fixed_rate < 0 ||
        expanded.financing.term_of_loan < 0 ||
        expanded.financing.total_interest < 0
      ) {
        return toast.error("Financing amounts and rates cannot be negative");
      }
    }

    setIsSubmitting(true);
    try {
      // Vehicle Availability Check (HIGH-10)
      if (vehicleId !== "none") {
        const { data: otherDeals, error: dealsErr } = await supabase
          .from("deals")
          .select("id, deal_number, title, stage")
          .eq("vehicle_id", vehicleId)
          .neq("id", deal?.id || '00000000-0000-0000-0000-000000000000')
          .in("stage", ["funded", "delivered"]);

        if (dealsErr) throw dealsErr;

        if (otherDeals && otherDeals.length > 0) {
          setIsSubmitting(false);
          return toast.error(`Vehicle is already sold/delivered in another deal (${otherDeals[0].deal_number})`);
        }
      }

      // Compute front_gross from vehicle purchase cost when available
      const linkedVehicle = vehicleId !== "none" ? vehicles.find(v => v.id === vehicleId) : null;
      const vehicleCost = linkedVehicle ? (Number(linkedVehicle.purchase_cost) || 0) : 0;
      const computedFrontGross = worksheet.selling_price - vehicleCost;

      // Set closed_at when deal reaches a terminal stage (funded/lost), clear when reverted
      const isTerminalStage = stage === "funded" || stage === "lost";
      const closedAt = isTerminalStage ? new Date().toISOString() : null;

      // Keep root deals table columns populated for backward compatibility / querying
      const payload = {
        title, customer_id: customerId, vehicle_id: vehicleId === "none" ? null : vehicleId, stage: stage as any,
        sale_price: worksheet.selling_price, trade_in_value: expanded.trade_in.trade_in_allowance,
        down_payment: worksheet.deposit_amount, lender: expanded.financing.is_financing ? expanded.financing.financing_type : null,
        interest_rate: expanded.financing.fixed_rate, finance_term: expanded.financing.term_of_loan,
        monthly_payment: expanded.financing.regular_payments,
        front_gross: computedFrontGross,
        closed_at: closedAt,
        delivery_date: expanded.delivery.delivery_date || null,
        fi_notes: JSON.stringify(worksheet), fi_total: totalBalanceDue,
        notes: JSON.stringify(expanded)
      };

      if (deal) {
        const { error } = await supabase.from("deals").update(payload).eq("id", deal.id);
        if (error) throw error;
        toast.success("Deal updated");
        // Compute changes for audit log
        const dealChanges = computeChanges(
          { title: deal.title, customer_id: deal.customer_id, vehicle_id: deal.vehicle_id, stage: deal.stage, sale_price: deal.sale_price, trade_in_value: deal.trade_in_value, down_payment: deal.down_payment, lender: deal.lender, interest_rate: deal.interest_rate, finance_term: deal.finance_term, monthly_payment: deal.monthly_payment, front_gross: deal.front_gross, closed_at: deal.closed_at },
          { title, customer_id: customerId, vehicle_id: vehicleId === "none" ? null : vehicleId, stage, sale_price: worksheet.selling_price, trade_in_value: expanded.trade_in.trade_in_allowance, down_payment: worksheet.deposit_amount, lender: expanded.financing.is_financing ? expanded.financing.financing_type : null, interest_rate: expanded.financing.fixed_rate, finance_term: expanded.financing.term_of_loan, monthly_payment: expanded.financing.regular_payments, front_gross: computedFrontGross, closed_at: closedAt },
          ['title', 'customer_id', 'vehicle_id', 'stage', 'sale_price', 'trade_in_value', 'down_payment', 'lender', 'interest_rate', 'finance_term', 'monthly_payment', 'front_gross', 'closed_at']
        );

        // Compute sub-worksheet changes (HIGH-6)
        const oldWorksheet = parseWorksheet(deal.fi_notes);
        const worksheetChanges = computeChanges(
          oldWorksheet as any,
          worksheet as any,
          ['selling_price', 'omvic_fee', 'hst_rate', 'hst_amount', 'license_fee', 'gasoline', 'deposit_amount', 'deposit_method', 'lien_registration_fee', 'rst_on_insurance']
        );

        // Add comparison for line item sub-arrays in worksheet
        const formatLineItems = (items: LineItem[] | undefined | null): string => {
          if (!items || items.length === 0) return "None";
          return items.map(item => `${item.name || "Unnamed"} ($${(item.amount || 0).toLocaleString('en-CA')})`).join(", ");
        };

        const oldWarrantiesStr = formatLineItems(oldWorksheet.warranties);
        const newWarrantiesStr = formatLineItems(worksheet.warranties);
        if (oldWarrantiesStr !== newWarrantiesStr) {
          worksheetChanges['warranties'] = { from: oldWarrantiesStr, to: newWarrantiesStr };
        }

        const oldFeesStr = formatLineItems(oldWorksheet.fees);
        const newFeesStr = formatLineItems(worksheet.fees);
        if (oldFeesStr !== newFeesStr) {
          worksheetChanges['fees'] = { from: oldFeesStr, to: newFeesStr };
        }

        const oldAccessoriesStr = formatLineItems(oldWorksheet.accessories);
        const newAccessoriesStr = formatLineItems(worksheet.accessories);
        if (oldAccessoriesStr !== newAccessoriesStr) {
          worksheetChanges['accessories'] = { from: oldAccessoriesStr, to: newAccessoriesStr };
        }

        const oldInsurancesStr = formatLineItems(oldWorksheet.insurances);
        const newInsurancesStr = formatLineItems(worksheet.insurances);
        if (oldInsurancesStr !== newInsurancesStr) {
          worksheetChanges['insurances'] = { from: oldInsurancesStr, to: newInsurancesStr };
        }

        // Compute trade-in & financing sub-changes
        const oldExpanded = parseExpandedNotes(deal.notes);
        const tradeInChanges = computeChanges(
          (oldExpanded.trade_in || {}) as any,
          (expanded.trade_in || {}) as any,
          [
            'has_trade_in', 'year', 'make', 'model', 'body_type', 'trim_level',
            'colour', 'vin', 'exact_distance', 'distance_type', 'hst_registrant_no',
            'has_lien', 'lien_holder', 'lien_amount', 'trade_in_allowance'
          ]
        );
        const financingChanges = computeChanges(
          (oldExpanded.financing || {}) as any,
          (expanded.financing || {}) as any,
          [
            'is_financing', 'financing_type', 'date_of_advance', 'interest_start_date',
            'regular_payments', 'fixed_rate', 'term_of_loan', 'amortization_period',
            'total_number_of_payments', 'payment_frequency', 'date_of_first_payment',
            'date_of_last_payment_amount', 'remaining_balance', 'total_of_all_payments',
            'total_interest', 'apr', 'dealer_incentive'
          ]
        );
        const deliveryChanges = computeChanges(
          (oldExpanded.delivery || {}) as any,
          (expanded.delivery || {}) as any,
          ['delivery_date', 'delivery_notes', 'is_delivered', 'mfrs_warranty_date', 'safety_certificate', 'daily_rental', 'mto_brand']
        );
        const guarantyChanges = computeChanges(
          (oldExpanded.guaranty || {}) as any,
          (expanded.guaranty || {}) as any,
          ['has_dealer_guaranty', 'days_or_km', 'description', 'has_extended_warranty', 'retail_value', 'company', 'months_or_km', 'warranty_description']
        );
        const insuranceChanges = computeChanges(
          (oldExpanded.insurance || {}) as any,
          (expanded.insurance || {}) as any,
          ['company', 'policy_no', 'expiry_date', 'agent_and_phone']
        );
        const rootExpandedChanges = computeChanges(
          { disclosures: oldExpanded.disclosures || "", comments: oldExpanded.comments || "" },
          { disclosures: expanded.disclosures || "", comments: expanded.comments || "" },
          ['disclosures', 'comments']
        );

        const formattedTradeIn: any = {};
        Object.entries(tradeInChanges).forEach(([k, v]) => {
          const formattedKey = k === 'trade_in_allowance' ? 'trade_in_allowance' : `trade_in_${k}`;
          formattedTradeIn[formattedKey] = v;
        });
        const formattedFinancing: any = {};
        Object.entries(financingChanges).forEach(([k, v]) => {
          formattedFinancing[`finance_${k}`] = v;
        });
        const formattedDelivery: any = {};
        Object.entries(deliveryChanges).forEach(([k, v]) => {
          formattedDelivery[`delivery_${k}`] = v;
        });
        const formattedGuaranty: any = {};
        Object.entries(guarantyChanges).forEach(([k, v]) => {
          formattedGuaranty[`guaranty_${k}`] = v;
        });
        const formattedInsurance: any = {};
        Object.entries(insuranceChanges).forEach(([k, v]) => {
          formattedInsurance[`insurance_${k}`] = v;
        });

        const combinedChanges = {
          ...dealChanges,
          ...worksheetChanges,
          ...formattedTradeIn,
          ...formattedFinancing,
          ...formattedDelivery,
          ...formattedGuaranty,
          ...formattedInsurance,
          ...rootExpandedChanges
        };

        if (Object.keys(combinedChanges).length > 0) {
          await logAudit({
            entityType: 'deal',
            entityId: deal.id,
            action: 'updated',
            changes: combinedChanges,
            metadata: { title, deal_number: deal.deal_number, stage },
            performedBy: user?.id,
          });
        }
      } else {
        const { data: userData } = await supabase.auth.getUser();
        
        // Generate custom deal number on the client if prefix is configured
        const prefix = localStorage.getItem("deal_number_prefix") || "D";
        const { data: existingDeals } = await supabase.from("deals").select("deal_number");
        let nextSeq = 1;
        if (existingDeals && existingDeals.length > 0) {
          const numbers = existingDeals
            .map(d => {
              if (d.deal_number && d.deal_number.startsWith(prefix)) {
                const numPart = d.deal_number.substring(prefix.length);
                const parsed = parseInt(numPart, 10);
                return isNaN(parsed) ? 0 : parsed;
              }
              return 0;
            });
          const maxNum = Math.max(...numbers, 0);
          nextSeq = maxNum + 1;
        }
        const generatedDealNumber = `${prefix}${String(nextSeq).padStart(3, '0')}`;

        const { data: newDeal, error } = await supabase.from("deals").insert({ 
          ...payload, 
          deal_number: generatedDealNumber,
          assigned_to: userData?.user?.id 
        } as any).select().single();

        if (error) throw error;
        toast.success("Deal created");
        await logAudit({
          entityType: 'deal',
          entityId: newDeal?.id,
          action: 'created',
          metadata: { title, deal_number: newDeal?.deal_number, stage },
          performedBy: user?.id,
        });
      }

      if (vehicleId !== "none") {
        if (stage === "funded" || stage === "delivered") {
          await supabase.from("vehicles").update({ status: "sold", sold_price: worksheet.selling_price, hst_collected: hstAmount, trade_in_value: expanded.trade_in.has_trade_in ? expanded.trade_in.trade_in_allowance : 0, sold_date: expanded.delivery.delivery_date || new Date().toISOString().split('T')[0], sold_to_customer_id: customerId }).eq("id", vehicleId);
        } else {
          await supabase.from("vehicles").update({ status: "available", sold_price: null, sold_date: null, sold_to_customer_id: null }).eq("id", vehicleId);
        }
      }
      onSaved();
    } catch (err) { toast.error("Failed to save deal"); } finally { setIsSubmitting(false); }
  };

  useKeyboardShortcut(["ctrl", "s"], handleSubmit);

  const handleGenerateBillOfSale = async () => {
    if (!customerId) return toast.error("Select a customer first");

    const customer = customers.find(c => c.id === customerId);
    const vehicle = vehicleId !== "none" ? vehicles.find(v => v.id === vehicleId) : null;

    if (!customer) return toast.error("Customer not found");

    setIsGeneratingPdf(true);
    setBillOfSaleOpen(true);
    setBillOfSalePdf(null);

    try {
      const billData: BillOfSaleData = {
        customer: {
          first_name: customer.first_name,
          last_name: customer.last_name,
          email: customer.email,
          phone: customer.phone,
          address: customer.address,
          city: customer.city,
          province: customer.province,
          postal_code: customer.postal_code,
        },
        vehicle: vehicle ? {
          year: vehicle.year,
          make: vehicle.make,
          model: vehicle.model,
          color: vehicle.color,
          vin: vehicle.vin,
          mileage: vehicle.mileage,
          body_type: vehicle.body_type || '',
          trim: vehicle.trim || '',
          stock_number: vehicle.stock_number || '',
        } : null,
        worksheet: {
          selling_price: worksheet.selling_price,
          omvic_fee: worksheet.omvic_fee,
          hst_rate: worksheet.hst_rate,
          license_fee: worksheet.license_fee,
          gasoline: worksheet.gasoline,
          deposit_amount: worksheet.deposit_amount,
          deposit_method: worksheet.deposit_method,
          lien_registration_fee: worksheet.lien_registration_fee,
          rst_on_insurance: worksheet.rst_on_insurance,
          warranties: worksheet.warranties,
          fees: worksheet.fees,
          accessories: worksheet.accessories,
          insurances: worksheet.insurances,
        },
        tradeIn: expanded.trade_in,
        financing: expanded.financing,
        guaranty: expanded.guaranty,
        insurance: expanded.insurance,
        delivery: expanded.delivery,
        computed: {
          extWarrantyTotal,
          feesTotal,
          accessoriesTotal,
          insurancesTotal,
          subTotal1,
          netDifference,
          hstAmount,
          payoutLien,
          subTotal2,
          payableOnDelivery,
          totalPrincipalLoan,
          costOfBorrowing,
          totalBalanceDue,
        },
        comments: expanded.comments,
        dealDate: expanded.delivery.delivery_date || undefined,
        salespersonName: expanded.salesperson_name || undefined,
        salespersonRegNo: expanded.salesperson_reg_no || undefined,
        driverLicense: customer.driver_license || undefined,
        driverLicenseExpiry: customer.driver_license_expiry || undefined,
      };

      const pdfBytes = await generateBillOfSale(billData);
      setBillOfSalePdf(pdfBytes);
      toast.success("Bill of Sale generated");
    } catch (err) {
      console.error('Failed to generate Bill of Sale:', err);
      toast.error("Failed to generate Bill of Sale");
      setBillOfSaleOpen(false);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleExportDeal = async () => {
    const customer = customers.find(c => c.id === customerId);
    const vehicle = vehicles.find(v => v.id === vehicleId);
    const customerName = customer ? `${customer.first_name} ${customer.last_name}` : "Unknown";
    const vehicleName = vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : "No Vehicle";

    // Sheet 1: Deal Overview
    const overviewData: Record<string, string | number>[] = [
      { Item: "Deal Title", Value: title },
      { Item: "Stage", Value: stage },
      { Item: "Customer", Value: customerName },
      { Item: "Vehicle", Value: vehicleName },
      { Item: "VIN", Value: vehicle?.vin || "" },
    ];

    // Sheet 2: Terms of Settlement (Worksheet)
    const tradeInAllowance = expanded.trade_in.has_trade_in ? expanded.trade_in.trade_in_allowance : 0;
    const worksheetData: Record<string, string | number>[] = [
      { Item: "Selling Price", Amount: worksheet.selling_price },
      { Item: "Extended Warranty (Subtotal)", Amount: extWarrantyTotal },
    ];
    worksheet.warranties.forEach(w => {
      worksheetData.push({ Item: `  Warranty: ${w.name || "Unnamed"}`, Amount: w.amount });
    });
    worksheetData.push(
      { Item: "OMVIC Fee", Amount: worksheet.omvic_fee },
      { Item: "Fees (Subtotal)", Amount: feesTotal },
    );
    worksheet.fees.forEach(f => {
      worksheetData.push({ Item: `  Fee: ${f.name || "Unnamed"}`, Amount: f.amount });
    });
    worksheetData.push({ Item: "Accessories (Subtotal)", Amount: accessoriesTotal });
    worksheet.accessories.forEach(a => {
      worksheetData.push({ Item: `  Accessory: ${a.name || "Unnamed"}`, Amount: a.amount });
    });
    worksheetData.push(
      { Item: "", Amount: "" as any },
      { Item: "Sub-Total 1", Amount: subTotal1 },
      { Item: "Deduct Trade-In Allowance", Amount: -tradeInAllowance },
      { Item: "Net Difference", Amount: netDifference },
      { Item: `HST (${worksheet.hst_rate}%)`, Amount: hstAmount },
      { Item: "Licence Fee", Amount: worksheet.license_fee },
      { Item: "Gasoline", Amount: worksheet.gasoline },
      { Item: "Payout Lien on Trade-In", Amount: payoutLien },
      { Item: "Sub-Total 2", Amount: subTotal2 },
      { Item: "", Amount: "" as any },
      { Item: "Deposit", Amount: worksheet.deposit_amount },
      { Item: "Deposit Method", Amount: worksheet.deposit_method as any },
      { Item: "Payable on Delivery", Amount: payableOnDelivery },
      { Item: "", Amount: "" as any },
      { Item: "Insurance Add-ons (Subtotal)", Amount: insurancesTotal },
    );
    worksheet.insurances.forEach(ins => {
      worksheetData.push({ Item: `  Insurance: ${ins.name || "Unnamed"}`, Amount: ins.amount });
    });
    worksheetData.push(
      { Item: "R.S.T. on Insurance", Amount: worksheet.rst_on_insurance },
      { Item: "Lien Registration Fee", Amount: worksheet.lien_registration_fee },
      { Item: "Total (Principal) Amount of Loan", Amount: totalPrincipalLoan },
      { Item: "Cost of Borrowing (Interest)", Amount: costOfBorrowing },
      { Item: "", Amount: "" as any },
      { Item: "TOTAL BALANCE DUE", Amount: totalBalanceDue },
    );

    // Sheet 3: Vehicle Purchase & Expenses
    const vehicleFinData: Record<string, string | number>[] = [];
    if (vehicle) {
      vehicleFinData.push(
        { Item: "Purchase Cost (excl. HST)", Amount: Number(vehicle.purchase_cost) || 0 },
        { Item: "HST Paid on Purchase", Amount: Number(vehicle.purchase_hst) || 0 },
        { Item: "Purchase Date", Amount: vehicle.purchase_date || "" },
        { Item: "", Amount: "" as any },
      );

      // Fetch vehicle expenses
      const { data: expenses } = await supabase
        .from("vehicle_expenses")
        .select("*")
        .eq("vehicle_id", vehicleId)
        .is("deleted_at", null)
        .order("expense_date", { ascending: false });

      if (expenses && expenses.length > 0) {
        vehicleFinData.push({ Item: "--- EXPENSES ---", Amount: "" as any });
        let totalExpAmt = 0;
        let totalExpHst = 0;
        expenses.forEach(e => {
          const amt = Number(e.amount);
          const hst = Number(e.hst_amount);
          totalExpAmt += amt;
          totalExpHst += hst;
          vehicleFinData.push({
            Item: `${e.category}${e.description ? ` - ${e.description}` : ""} (${e.expense_date})`,
            Amount: amt,
          });
        });
        vehicleFinData.push(
          { Item: "", Amount: "" as any },
          { Item: "Total Expenses (excl. HST)", Amount: totalExpAmt },
          { Item: "Total HST on Expenses", Amount: totalExpHst },
          { Item: "", Amount: "" as any },
          { Item: "Total Invested (Purchase + Expenses)", Amount: (Number(vehicle.purchase_cost) || 0) + totalExpAmt },
        );
      }
    }

    // Sheet 4: Trade-In Details
    const tradeInData: Record<string, string | number>[] = [];
    if (expanded.trade_in.has_trade_in) {
      tradeInData.push(
        { Item: "Year", Value: expanded.trade_in.year },
        { Item: "Make", Value: expanded.trade_in.make },
        { Item: "Model", Value: expanded.trade_in.model },
        { Item: "Body Type", Value: expanded.trade_in.body_type },
        { Item: "Trim Level", Value: expanded.trade_in.trim_level },
        { Item: "Colour", Value: expanded.trade_in.colour },
        { Item: "VIN", Value: expanded.trade_in.vin },
        { Item: "Distance", Value: `${expanded.trade_in.exact_distance} ${expanded.trade_in.distance_type}` },
        { Item: "Trade-In Allowance", Value: expanded.trade_in.trade_in_allowance },
        { Item: "Has Lien", Value: expanded.trade_in.has_lien ? "Yes" : "No" },
      );
      if (expanded.trade_in.has_lien) {
        tradeInData.push(
          { Item: "Lien Holder", Value: expanded.trade_in.lien_holder },
          { Item: "Lien Amount", Value: expanded.trade_in.lien_amount },
        );
      }
    }

    // Sheet 5: Financing
    const financingData: Record<string, string | number>[] = [];
    if (expanded.financing.is_financing) {
      financingData.push(
        { Item: "Financing Type", Value: expanded.financing.financing_type },
        { Item: "Date of Advance", Value: expanded.financing.date_of_advance },
        { Item: "Interest Start Date", Value: expanded.financing.interest_start_date },
        { Item: "Regular Payments", Value: expanded.financing.regular_payments },
        { Item: "Fixed Rate (%)", Value: expanded.financing.fixed_rate },
        { Item: "Term of Loan (months)", Value: expanded.financing.term_of_loan },
        { Item: "Amortization (months)", Value: expanded.financing.amortization_period },
        { Item: "Total # of Payments", Value: expanded.financing.total_number_of_payments },
        { Item: "Payment Frequency", Value: expanded.financing.payment_frequency },
        { Item: "Date of First Payment", Value: expanded.financing.date_of_first_payment },
        { Item: "Date of Last Payment & Amt", Value: expanded.financing.date_of_last_payment_amount },
        { Item: "Remaining Balance", Value: expanded.financing.remaining_balance },
        { Item: "Total of All Payments", Value: expanded.financing.total_of_all_payments },
        { Item: "Total Interest", Value: expanded.financing.total_interest },
        { Item: "APR (%)", Value: expanded.financing.apr },
        { Item: "Dealer Incentive", Value: expanded.financing.dealer_incentive ? "Yes" : "No" },
      );
    }

    const sheets = [
      { name: "Deal Overview", data: overviewData },
      { name: "Terms of Settlement", data: worksheetData },
    ];
    if (vehicleFinData.length > 0) sheets.push({ name: "Vehicle & Expenses", data: vehicleFinData });
    if (tradeInData.length > 0) sheets.push({ name: "Trade-In", data: tradeInData });
    if (financingData.length > 0) sheets.push({ name: "Financing", data: financingData });

    const safeTitle = title.replace(/[^a-zA-Z0-9 _-]/g, "").replace(/\s+/g, "_") || "Deal";
    await exportToExcel(sheets, `Deal_${safeTitle}.xlsx`);
    toast.success("Deal exported successfully");
  };

  const renderLineItemSection = (label: string, section: "warranties" | "fees" | "accessories" | "insurances") => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold">{label}</Label>
        <Button type="button" variant="outline" size="sm" onClick={() => addLineItem(section)}><Plus className="h-3 w-3 mr-1" /> Add</Button>
      </div>
      {worksheet[section].map(item => (
        <div key={item.id} className="flex gap-2 items-center">
          <Input placeholder="Name / Description" value={item.name} onChange={e => updateLineItem(section, item.id, "name", e.target.value)} className="flex-1" />
          <Input type="number" step="0.01" value={item.amount || ""} onChange={e => updateLineItem(section, item.id, "amount", e.target.value)} className="w-28" />
          <Button type="button" variant="ghost" size="icon" className="text-destructive h-8 w-8 shrink-0" onClick={() => removeLineItem(section, item.id)}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1"><h1 className="text-2xl md:text-3xl font-heading font-bold">{deal ? "Edit Deal" : "New Deal"}</h1></div>
        <Select value={stage} onValueChange={handleStageChange}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>{DEAL_STAGES.map(s => (<SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>))}</SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="pt-5 pb-4">
          <Label>Deal Title *</Label>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. John D. — 2023 Civic" className="mt-2" />
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full flex overflow-x-auto justify-start sm:grid sm:grid-cols-6 h-auto p-1 scrollbar-thin">
          <TabsTrigger value="customers" className="whitespace-nowrap flex-shrink-0 text-xs sm:text-sm"><User className="h-3.5 w-3.5 mr-1 hidden xl:inline" />Customer</TabsTrigger>
          <TabsTrigger value="vehicles" className="whitespace-nowrap flex-shrink-0 text-xs sm:text-sm"><Car className="h-3.5 w-3.5 mr-1 hidden xl:inline" />Vehicle & Trade</TabsTrigger>
          <TabsTrigger value="worksheet" className="whitespace-nowrap flex-shrink-0 text-xs sm:text-sm"><Calculator className="h-3.5 w-3.5 mr-1 hidden xl:inline" />Worksheet</TabsTrigger>
          <TabsTrigger value="financing" className="whitespace-nowrap flex-shrink-0 text-xs sm:text-sm"><Calculator className="h-3.5 w-3.5 mr-1 hidden xl:inline" />Financing</TabsTrigger>
          <TabsTrigger value="guaranty" className="whitespace-nowrap flex-shrink-0 text-xs sm:text-sm"><ShieldCheck className="h-3.5 w-3.5 mr-1 hidden xl:inline" />Ins & Guar</TabsTrigger>
          <TabsTrigger value="delivery" className="whitespace-nowrap flex-shrink-0 text-xs sm:text-sm"><Truck className="h-3.5 w-3.5 mr-1 hidden xl:inline" />Delivery</TabsTrigger>
        </TabsList>

        <TabsContent value="customers" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Purchaser Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Label>Select Customer *</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger><SelectValue placeholder="Choose a customer..." /></SelectTrigger>
                <SelectContent>
                  {customers.map(c => (<SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">Note: Address, phone, and DL details are inherited from the Customer profile.</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vehicles" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Vehicle Selection</CardTitle></CardHeader>
            <CardContent>
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger><SelectValue placeholder="Choose a vehicle..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {vehicles.filter(v => v.status === "available" || (deal && deal.vehicle_id === v.id) || vehicleId === v.id).map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.year} {v.make} {v.model}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Vehicle To Be Traded-In</CardTitle>
              <Switch checked={expanded.trade_in.has_trade_in} onCheckedChange={v => updateExpanded("trade_in", "has_trade_in", v)} />
            </CardHeader>
            {expanded.trade_in.has_trade_in && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div><Label>Year</Label><Input value={expanded.trade_in.year} onChange={e => updateExpanded("trade_in", "year", e.target.value)} /></div>
                  <div><Label>Make</Label><Input value={expanded.trade_in.make} onChange={e => updateExpanded("trade_in", "make", e.target.value)} /></div>
                  <div><Label>Model</Label><Input value={expanded.trade_in.model} onChange={e => updateExpanded("trade_in", "model", e.target.value)} /></div>
                  <div><Label>Body Type</Label><Input value={expanded.trade_in.body_type} onChange={e => updateExpanded("trade_in", "body_type", e.target.value)} /></div>
                  <div><Label>Trim Level</Label><Input value={expanded.trade_in.trim_level} onChange={e => updateExpanded("trade_in", "trim_level", e.target.value)} /></div>
                  <div><Label>Colour</Label><Input value={expanded.trade_in.colour} onChange={e => updateExpanded("trade_in", "colour", e.target.value)} /></div>
                  <div className="col-span-2"><Label>VIN</Label><Input value={expanded.trade_in.vin} onChange={e => updateExpanded("trade_in", "vin", e.target.value)} /></div>
                  <div><Label>Distance</Label><Input value={expanded.trade_in.exact_distance} onChange={e => updateExpanded("trade_in", "exact_distance", e.target.value)} /></div>
                  <div><Label>Units</Label><Select value={expanded.trade_in.distance_type} onValueChange={v => updateExpanded("trade_in", "distance_type", v)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="kms">KMS</SelectItem><SelectItem value="miles">MILES</SelectItem></SelectContent></Select></div>
                  <div className="col-span-2"><Label>HST Registrant No.</Label><Input value={expanded.trade_in.hst_registrant_no} onChange={e => updateExpanded("trade_in", "hst_registrant_no", e.target.value)} /></div>
                </div>
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-4"><Label>Is there a lien against this vehicle?</Label><Switch checked={expanded.trade_in.has_lien} onCheckedChange={v => updateExpanded("trade_in", "has_lien", v)} /></div>
                  {expanded.trade_in.has_lien && (
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Lien Holder</Label><Input value={expanded.trade_in.lien_holder} onChange={e => updateExpanded("trade_in", "lien_holder", e.target.value)} /></div>
                      <div><Label>Lien Amount ($)</Label><Input type="number" value={expanded.trade_in.lien_amount || ""} onChange={e => updateExpanded("trade_in", "lien_amount", Number(e.target.value))} /></div>
                    </div>
                  )}
                </div>
                <div className="border-t pt-4">
                  <div><Label>Trade-In Allowance ($)</Label><Input type="number" value={expanded.trade_in.trade_in_allowance || ""} onChange={e => updateExpanded("trade_in", "trade_in_allowance", Number(e.target.value))} className="text-lg font-bold" /></div>
                </div>
              </CardContent>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="worksheet" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Terms of Settlement (Worksheet)</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <div className="space-y-4">
                  <div className="flex justify-between items-center"><Label>Selling Price</Label><Input type="number" className="w-32 text-right" value={worksheet.selling_price || ""} onChange={e => updateWorksheet("selling_price", Number(e.target.value))} /></div>
                  <div className="flex justify-between items-center"><Label className="text-muted-foreground">Extended Warranty (Subtotal)</Label><span className="w-32 text-right py-2">{formatCurrency(extWarrantyTotal)}</span></div>
                  <div className="flex justify-between items-center"><Label>OMVIC Fee</Label><Input type="number" className="w-32 text-right" value={worksheet.omvic_fee || ""} onChange={e => updateWorksheet("omvic_fee", Number(e.target.value))} /></div>
                  <div className="flex justify-between items-center"><Label className="text-muted-foreground">Fees & Accessories (Subtotal)</Label><span className="w-32 text-right py-2">{formatCurrency(feesTotal + accessoriesTotal)}</span></div>
                  <div className="flex justify-between items-center border-t border-b py-2 bg-muted/30"><Label className="font-bold">Sub-Total 1</Label><span className="w-32 text-right font-bold">{formatCurrency(subTotal1)}</span></div>
                  
                  <div className="flex justify-between items-center text-red-600"><Label>Deduct Trade-In Allowance</Label><span className="w-32 text-right py-2">- {formatCurrency(expanded.trade_in.has_trade_in ? expanded.trade_in.trade_in_allowance : 0)}</span></div>
                  <div className="flex justify-between items-center border-b pb-2"><Label className="font-bold">Net Difference</Label><span className="w-32 text-right font-bold">{formatCurrency(netDifference)}</span></div>
                  
                  <div className="flex justify-between items-center"><Label>HST Rate (%)</Label><Input type="number" className="w-32 text-right" value={worksheet.hst_rate} onChange={e => updateWorksheet("hst_rate", Number(e.target.value))} /></div>
                  <div className="flex justify-between items-center"><Label>H.S.T. on Net Difference</Label><span className="w-32 text-right py-2">{formatCurrency(hstAmount)}</span></div>
                  
                  <div className="flex justify-between items-center"><Label>Licence Fee</Label><Input type="number" className="w-32 text-right" value={worksheet.license_fee || ""} onChange={e => updateWorksheet("license_fee", Number(e.target.value))} /></div>
                  <div className="flex justify-between items-center"><Label>Gasoline</Label><Input type="number" className="w-32 text-right" value={worksheet.gasoline || ""} onChange={e => updateWorksheet("gasoline", Number(e.target.value))} /></div>
                  <div className="flex justify-between items-center"><Label>Payout Lien on Trade-In</Label><span className="w-32 text-right py-2">{formatCurrency(payoutLien)}</span></div>
                  
                  <div className="flex justify-between items-center border-t border-b py-2 bg-muted/30"><Label className="font-bold">Sub-Total 2</Label><span className="w-32 text-right font-bold">{formatCurrency(subTotal2)}</span></div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center"><Label>Deposit</Label><Input type="number" className="w-32 text-right" value={worksheet.deposit_amount || ""} onChange={e => updateWorksheet("deposit_amount", Number(e.target.value))} /></div>
                  <div className="flex justify-between items-center"><Label>Deposit Method</Label>
                    <Select value={worksheet.deposit_method} onValueChange={v => updateWorksheet("deposit_method", v)}>
                      <SelectTrigger className="w-32"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="credit_card">Credit Card</SelectItem><SelectItem value="eft">EFT</SelectItem><SelectItem value="cheque">Cheque</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="debit">Debit</SelectItem></SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex justify-between items-center border-t border-b py-2 bg-muted/30"><Label className="font-bold">Payable on Delivery</Label><span className="w-32 text-right font-bold">{formatCurrency(payableOnDelivery)}</span></div>

                  <div className="flex justify-between items-center"><Label className="text-muted-foreground">Insurance Add-ons (Subtotal)</Label><span className="w-32 text-right py-2">{formatCurrency(insurancesTotal)}</span></div>
                  <div className="flex justify-between items-center"><Label>R.S.T. on Insurance</Label><Input type="number" className="w-32 text-right" value={worksheet.rst_on_insurance || ""} onChange={e => updateWorksheet("rst_on_insurance", Number(e.target.value))} /></div>
                  <div className="flex justify-between items-center"><Label>Lien Registration Fee</Label><Input type="number" className="w-32 text-right" value={worksheet.lien_registration_fee || ""} onChange={e => updateWorksheet("lien_registration_fee", Number(e.target.value))} /></div>

                  <div className="flex justify-between items-center border-t border-b py-2 bg-muted/30"><Label className="font-bold">Total (Principal) Amount of Loan</Label><span className="w-32 text-right font-bold text-primary">{formatCurrency(totalPrincipalLoan)}</span></div>
                  
                  <div className="flex justify-between items-center"><Label>Cost of Borrowing (Interest)</Label><span className="w-32 text-right py-2">{formatCurrency(costOfBorrowing)}</span></div>

                  <div className="flex justify-between items-center border-t-2 border-primary py-4 bg-primary/5 rounded-b"><Label className="font-heading font-bold text-lg text-primary pl-2">TOTAL BALANCE DUE</Label><span className="w-32 text-right font-heading font-bold text-2xl text-primary pr-2">{formatCurrency(totalBalanceDue)}</span></div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card><CardHeader><CardTitle className="text-base">Line Items</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              {renderLineItemSection("Warranties", "warranties")}<div className="border-t" />
              {renderLineItemSection("Fees", "fees")}<div className="border-t" />
              {renderLineItemSection("Accessories", "accessories")}<div className="border-t" />
              {renderLineItemSection("Insurances", "insurances")}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="financing" className="mt-4">
          <Card>
             <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Terms of Financing</CardTitle>
              <Switch checked={expanded.financing.is_financing} onCheckedChange={v => updateExpanded("financing", "is_financing", v)} />
            </CardHeader>
            {expanded.financing.is_financing && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="col-span-2"><Label>Financing Provider</Label><Select value={expanded.financing.financing_type} onValueChange={v => updateExpanded("financing", "financing_type", v)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="third_party">Third-Party Financing</SelectItem><SelectItem value="dealer">Dealer Financing</SelectItem></SelectContent></Select></div>
                  <div><Label>Date of Advance</Label><Input type="date" value={expanded.financing.date_of_advance} onChange={e => updateExpanded("financing", "date_of_advance", e.target.value)} /></div>
                  <div><Label>Interest Start Date</Label><Input type="date" value={expanded.financing.interest_start_date} onChange={e => updateExpanded("financing", "interest_start_date", e.target.value)} /></div>
                  
                  <div><Label>Regular Payments $</Label><Input type="number" value={expanded.financing.regular_payments || ""} onChange={e => updateExpanded("financing", "regular_payments", Number(e.target.value))} /></div>
                  <div><Label>Fixed Rate %</Label><Input type="number" step="0.01" value={expanded.financing.fixed_rate || ""} onChange={e => updateExpanded("financing", "fixed_rate", Number(e.target.value))} /></div>
                  <div><Label>Term of Loan (months)</Label><Input type="number" value={expanded.financing.term_of_loan || ""} onChange={e => updateExpanded("financing", "term_of_loan", Number(e.target.value))} /></div>
                  <div><Label>Amortization (months)</Label><Input type="number" value={expanded.financing.amortization_period || ""} onChange={e => updateExpanded("financing", "amortization_period", Number(e.target.value))} /></div>

                  <div><Label>Total # of Payments</Label><Input type="number" value={expanded.financing.total_number_of_payments || ""} onChange={e => updateExpanded("financing", "total_number_of_payments", Number(e.target.value))} /></div>
                  <div><Label>Payment Frequency</Label><Select value={expanded.financing.payment_frequency} onValueChange={v => updateExpanded("financing", "payment_frequency", v)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="Weekly">Weekly</SelectItem><SelectItem value="Bi-Weekly">Bi-Weekly</SelectItem><SelectItem value="Monthly">Monthly</SelectItem></SelectContent></Select></div>
                  <div><Label>Date of First Payment</Label><Input type="date" value={expanded.financing.date_of_first_payment} onChange={e => updateExpanded("financing", "date_of_first_payment", e.target.value)} /></div>
                  <div><Label>Date of Last Pmt & Amt</Label><Input value={expanded.financing.date_of_last_payment_amount} onChange={e => updateExpanded("financing", "date_of_last_payment_amount", e.target.value)} /></div>

                  <div><Label>Remaining Balance</Label><Input type="number" value={expanded.financing.remaining_balance || ""} onChange={e => updateExpanded("financing", "remaining_balance", Number(e.target.value))} /></div>
                  <div><Label>Total of All Payments $</Label><Input type="number" value={expanded.financing.total_of_all_payments || ""} onChange={e => updateExpanded("financing", "total_of_all_payments", Number(e.target.value))} /></div>
                  <div><Label>Total Interest $</Label><Input type="number" value={expanded.financing.total_interest || ""} onChange={e => updateExpanded("financing", "total_interest", Number(e.target.value))} /></div>
                  <div><Label>Annual Perc. Rate %</Label><Input type="number" step="0.01" value={expanded.financing.apr || ""} onChange={e => updateExpanded("financing", "apr", Number(e.target.value))} /></div>
                </div>
                <div className="border-t pt-4 flex items-center justify-between">
                  <Label>Will the dealer or salesperson receive any incentive for financing this vehicle?</Label>
                  <Switch checked={expanded.financing.dealer_incentive} onCheckedChange={v => updateExpanded("financing", "dealer_incentive", v)} />
                </div>
              </CardContent>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="guaranty" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Insurance Information</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-2"><Label>Name of Insurance Company</Label><Input value={expanded.insurance.company} onChange={e => updateExpanded("insurance", "company", e.target.value)} /></div>
                <div><Label>Policy No.</Label><Input value={expanded.insurance.policy_no} onChange={e => updateExpanded("insurance", "policy_no", e.target.value)} /></div>
                <div><Label>Expiry Date</Label><Input type="date" value={expanded.insurance.expiry_date} onChange={e => updateExpanded("insurance", "expiry_date", e.target.value)} /></div>
                <div className="col-span-4"><Label>Insurance Agent & Phone No.</Label><Input value={expanded.insurance.agent_and_phone} onChange={e => updateExpanded("insurance", "agent_and_phone", e.target.value)} /></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between"><CardTitle className="text-base">Dealer Guaranty</CardTitle><Switch checked={expanded.guaranty.has_dealer_guaranty} onCheckedChange={v => updateExpanded("guaranty", "has_dealer_guaranty", v)} /></CardHeader>
            {expanded.guaranty.has_dealer_guaranty && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Days or KM</Label><Input value={expanded.guaranty.days_or_km} onChange={e => updateExpanded("guaranty", "days_or_km", e.target.value)} /></div>
                  <div className="col-span-2"><Label>Description</Label><Input value={expanded.guaranty.description} onChange={e => updateExpanded("guaranty", "description", e.target.value)} /></div>
                </div>
              </CardContent>
            )}
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between"><CardTitle className="text-base">Extended Warranty (Details)</CardTitle><Switch checked={expanded.guaranty.has_extended_warranty} onCheckedChange={v => updateExpanded("guaranty", "has_extended_warranty", v)} /></CardHeader>
            {expanded.guaranty.has_extended_warranty && (
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Retail Value $</Label><Input type="number" value={expanded.guaranty.retail_value || ""} onChange={e => updateExpanded("guaranty", "retail_value", Number(e.target.value))} /></div>
                  <div><Label>Company</Label><Input value={expanded.guaranty.company} onChange={e => updateExpanded("guaranty", "company", e.target.value)} /></div>
                  <div><Label>Months or KM</Label><Input value={expanded.guaranty.months_or_km} onChange={e => updateExpanded("guaranty", "months_or_km", e.target.value)} /></div>
                  <div className="col-span-2"><Label>Description</Label><Input value={expanded.guaranty.warranty_description} onChange={e => updateExpanded("guaranty", "warranty_description", e.target.value)} /></div>
                </div>
              </CardContent>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="delivery" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Delivery & Disclosures</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><Label>Delivery Date</Label><Input type="date" value={expanded.delivery.delivery_date} onChange={e => updateExpanded("delivery", "delivery_date", e.target.value)} /></div>
                <div><Label>Mfrs Warranty In-Service</Label><Input type="date" value={expanded.delivery.mfrs_warranty_date} onChange={e => updateExpanded("delivery", "mfrs_warranty_date", e.target.value)} /></div>
                <div><Label>MTO Brand</Label><Input value={expanded.delivery.mto_brand} onChange={e => updateExpanded("delivery", "mto_brand", e.target.value)} placeholder="e.g. None" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t">
                <div className="flex items-center gap-3"><Switch checked={expanded.delivery.is_delivered} onCheckedChange={v => updateExpanded("delivery", "is_delivered", v)} /><Label>Vehicle Delivered</Label></div>
                <div className="flex items-center gap-3"><Switch checked={expanded.delivery.safety_certificate} onCheckedChange={v => updateExpanded("delivery", "safety_certificate", v)} /><Label>Safety Standards Cert.</Label></div>
                <div className="flex items-center gap-3"><Switch checked={expanded.delivery.daily_rental} onCheckedChange={v => updateExpanded("delivery", "daily_rental", v)} /><Label>Daily Rental</Label></div>
              </div>
              <div className="pt-4 border-t space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Salesperson Name</Label><Input value={expanded.salesperson_name} onChange={e => setExpanded(p => ({...p, salesperson_name: e.target.value}))} placeholder="e.g. John Smith" /></div>
                  <div><Label>Salesperson Registration No.</Label><Input value={expanded.salesperson_reg_no} onChange={e => setExpanded(p => ({...p, salesperson_reg_no: e.target.value}))} placeholder="e.g. 1234567" /></div>
                </div>
                <div><Label>Delivery Notes</Label><Textarea value={expanded.delivery.delivery_notes} onChange={e => updateExpanded("delivery", "delivery_notes", e.target.value)} rows={3} /></div>
                <div><Label>Comments (Repairs, Inducements, etc)</Label><Textarea value={expanded.comments} onChange={e => setExpanded(p => ({...p, comments: e.target.value}))} rows={4} placeholder="Identify any items, inducements or specific repairs included in the sale price..." /></div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <BillOfSalePreview
        open={billOfSaleOpen}
        onClose={() => setBillOfSaleOpen(false)}
        pdfBytes={billOfSalePdf}
        dealNumber={deal?.deal_number}
        isGenerating={isGeneratingPdf}
      />

      <div className="flex justify-between gap-3 pt-2 border-t">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExportDeal} disabled={!deal}>
            <Download className="h-4 w-4 mr-2" /> Export Deal
          </Button>
          <Button variant="outline" size="sm" onClick={handleGenerateBillOfSale} disabled={!customerId}>
            <FileText className="h-4 w-4 mr-2" /> Bill of Sale
          </Button>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} className="min-w-[120px]">
            {isSubmitting ? "Saving..." : deal ? "Save Changes" : "Create Deal"}
          </Button>
        </div>
      </div>  
    </div>
  );
}
